<template>
  <v-container>
    <v-row
      ><v-col cols="0" lg="1"></v-col
      ><v-col cols="12" lg="12" xl="10">
        <!--統計總覽-->
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4">
              <div class="grey--text text--darken-1 text-subtitle-2">
                員工總數
              </div>
              <div class="text-h4 font-weight-black text--primary">
                {{ totalEmployeeCount.toLocaleString() }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4">
              <div class="grey--text text--darken-1 text-subtitle-2">
                已註冊人數
              </div>
              <div class="text-h4 font-weight-black primary--text">
                {{ totalRegisteredCount.toLocaleString() }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4">
              <div class="grey--text text--darken-1 text-subtitle-2">
                整體註冊率
              </div>
              <div
                class="text-h4 font-weight-black"
                :class="`${percentageColor(totalPercentage)}--text`"
              >
                {{ totalPercentage.toFixed(2) }}%
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          ref="vDataTable"
          :headers="headers"
          :items="desserts"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          :search="search"
          :loading="isLoading"
          loading-text="資料讀取中..."
          no-data-text="暫時沒有符合的資料"
          item-key="companyCode"
          show-expand
          :items-per-page="-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>註冊率總覽</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                color="blue-grey"
                style="height: 40px"
                depressed
                dark
                class="mb-2 mt-2"
                :loading="isLoading"
                @click="getRegister"
              >
                重新整理
              </v-btn>
            </v-toolbar>
          </template>

          <template v-slot:[`item.employeeCount`]="{ item }">
            {{ item.employeeCount.toLocaleString() }}
          </template>
          <template v-slot:[`item.registeredCount`]="{ item }">
            {{ item.registeredCount.toLocaleString() }}
          </template>
          <template v-slot:[`item.unregisteredCount`]="{ item }">
            {{ item.unregisteredCount.toLocaleString() }}
          </template>
          <template v-slot:[`item.percentage`]="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :value="item.percentage"
                :color="percentageColor(item.percentage)"
                height="8"
                rounded
                style="min-width: 80px"
              ></v-progress-linear>
              <span
                class="ml-3 font-weight-bold"
                :class="`${percentageColor(item.percentage)}--text`"
                style="white-space: nowrap"
                >{{ item.registeredPercentage }}</span
              >
            </div>
          </template>

          <template v-slot:[`item.data-table-expand`]="props">
            <v-icon
              :class="{
                'v-data-table__expand-icon': true,
                'v-data-table__expand-icon--active':
                  props.isExpanded && transitioned[getItemId(props.item)],
              }"
              @click="toggleExpand(props)"
            >
              mdi-chevron-down
            </v-icon>
          </template>

          <!--展開內容 : 未註冊人員名單-->
          <template v-slot:[`expanded-item`]="{ headers, item }">
            <td
              :colspan="headers.length"
              :class="{
                'ma-0 pa-0': true,
                'expanded-closing': !transitioned[getItemId(item)],
              }"
              style="height: auto"
            >
              <v-expand-transition>
                <div v-show="transitioned[getItemId(item)]">
                  <div class="pa-2" style="min-height: 100px">
                    <v-container class="my-4">
                      <div class="d-flex align-center mb-3">
                        <div>
                          <div
                            class="grey--text text--darken-1 text-subtitle-2 font-weight-regular"
                          >
                            No.{{ item.companyCode }}
                          </div>
                          <p
                            class="text-h5 font-weight-black text--primary mb-0"
                          >
                            {{ item.companyName }} - 未註冊人員名單
                          </p>
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn
                          depressed
                          small
                          color="blue-grey"
                          class="white--text"
                          :disabled="!(unregistered[item.companyCode] || []).length"
                          @click="exportCsv(item)"
                        >
                          匯出 CSV
                        </v-btn>
                      </div>
                      <v-data-table
                        :headers="unregisteredHeaders"
                        :items="unregistered[item.companyCode] || []"
                        :loading="!!unregisteredLoading[item.companyCode]"
                        loading-text="名單讀取中..."
                        no-data-text="查無未註冊人員資料"
                        :items-per-page="10"
                        dense
                      ></v-data-table>
                    </v-container>
                  </div>
                </div>
              </v-expand-transition>
            </td>
          </template>
        </v-data-table></v-col
      ><v-col cols="0" lg="1"></v-col
    ></v-row>
  </v-container>
</template>

<script>
import { internalApiAxios } from "@/api";

// 未註冊人員名單 API
const UNREGISTERED_API = "/User/GetUserNotRegistered";

export default {
  data: () => ({
    search: "",
    expanded: [],
    singleExpand: false,
    isLoading: false,
    headers: [
      {
        text: "公司名稱",
        value: "companyName",
        align: "start",
      },
      {
        text: "公司代碼",
        value: "companyCode",
        align: "center",
        width: "12%",
      },
      {
        text: "員工數",
        value: "employeeCount",
        align: "end",
        width: "10%",
      },
      {
        text: "已註冊",
        value: "registeredCount",
        align: "end",
        width: "10%",
      },
      {
        text: "未註冊",
        value: "unregisteredCount",
        align: "end",
        width: "10%",
      },
      {
        text: "註冊率",
        value: "percentage",
        align: "center",
        width: "20%",
      },
      {
        text: "展開",
        value: "data-table-expand",
        sortable: false,
        align: "center",
        width: "6%",
      },
    ],
    unregisteredHeaders: [
      { text: "員工編號", value: "useR_NO", align: "center", width: "25%" },
      { text: "公司名稱", value: "companY_NAME", align: "start" },
      {
        text: "統一編號",
        value: "taX_ID_NUMBER",
        align: "center",
        width: "25%",
      },
    ],
    desserts: [],
    unregistered: {},
    unregisteredLoading: {},
    // companyCode 是 8 位數字，若用陣列存，Vue 的 $set 會把陣列撐到該索引長度而卡死
    transitioned: {},
    closeTimeouts: {},
  }),
  computed: {
    totalEmployeeCount() {
      return this.desserts.reduce((sum, i) => sum + i.employeeCount, 0);
    },
    totalRegisteredCount() {
      return this.desserts.reduce((sum, i) => sum + i.registeredCount, 0);
    },
    totalPercentage() {
      if (!this.totalEmployeeCount) return 0;
      return (this.totalRegisteredCount / this.totalEmployeeCount) * 100;
    },
  },
  watch: {
    singleExpand(v) {
      if (!v) return;
      this.expanded.forEach((item, i) => {
        if (i > 0) this.closeExpand(item);
      });
    },
  },

  created() {
    this.$bus.$emit("sideBarSelectedItem:push", 12);
    this.getRegister();
  },

  methods: {
    percentageColor(percentage) {
      if (percentage >= 60) return "success";
      if (percentage >= 30) return "warning";
      return "error";
    },
    async getRegister() {
      this.isLoading = true;
      await internalApiAxios({
        method: "post",
        url: "/User/GetRegister",
      })
        .then((res) => {
          const result = (res.data && res.data.result) || [];
          this.desserts = result.map((item) => {
            const employeeCount = Number(item.employeeCount) || 0;
            const registeredCount = Number(item.registeredCount) || 0;
            return {
              companyName: item.companyName,
              companyCode: item.companyCode,
              employeeCount,
              registeredCount,
              unregisteredCount: employeeCount - registeredCount,
              registeredPercentage: item.registeredPercentage,
              // 字串 "60.56%" 無法正確排序，另存數值欄位
              percentage: employeeCount
                ? (registeredCount / employeeCount) * 100
                : 0,
            };
          });
          if (this.desserts.length === 0) {
            this.$snackbar.add(`暫時沒有符合的資料`, { color: "error" });
          }
        })
        .catch((error) => {
          console.log(error);
          this.$snackbar.add(`取得註冊率失敗 : ${error}`, { color: "error" });
        });
      this.isLoading = false;
    },
    async getUnregistered(item) {
      const code = item.companyCode;
      if (this.unregistered[code] || this.unregisteredLoading[code]) return;
      this.$set(this.unregisteredLoading, code, true);
      await internalApiAxios({
        method: "post",
        url: `${UNREGISTERED_API}?companyNo=${encodeURIComponent(code)}`,
      })
        .then((res) => {
          // 回傳可能為陣列或包在 result 內
          const data = res.data;
          const result = Array.isArray(data) ? data : (data && data.result) || [];
          this.$set(this.unregistered, code, result);
        })
        .catch((error) => {
          console.log(error);
          this.$snackbar.add(`取得未註冊名單失敗 : ${error}`, {
            color: "error",
          });
        });
      this.$set(this.unregisteredLoading, code, false);
    },
    exportCsv(item) {
      const rows = this.unregistered[item.companyCode] || [];
      if (!rows.length) return;
      const cols = this.unregisteredHeaders;
      const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
      const csv = [
        cols.map((c) => escape(c.text)).join(","),
        ...rows.map((row) => cols.map((c) => escape(row[c.value])).join(",")),
      ].join("\r\n");
      // 加上 BOM 讓 Excel 正確辨識 UTF-8
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `未註冊名單_${item.companyName}_${item.companyCode}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    getItemId(item) {
      return item.companyCode;
    },
    toggleExpand(props) {
      const item = props.item;
      const id = this.getItemId(item);
      if (props.isExpanded && this.transitioned[id]) {
        // If we're expanded and not in the process of closing, close
        this.closeExpand(item);
      } else {
        // If we're closed or in the process of closing, expand
        clearTimeout(this.closeTimeouts[id]);
        props.expand(true);
        this.getUnregistered(item);
        this.$nextTick(() => this.$set(this.transitioned, id, true));
        if (this.singleExpand)
          this.$nextTick(() =>
            this.expanded.forEach((i) => i !== item && this.closeExpand(i))
          );
      }
    },
    closeExpand(item) {
      const id = this.getItemId(item);
      this.$set(this.transitioned, id, false);
      this.closeTimeouts[id] = setTimeout(
        () => this.$refs.vDataTable.expand(item, false),
        600
      );
    },
  },
};
</script>
<style></style>
